import { MultiSelectOption } from '@dispatch/shared/types';
import {
  Autocomplete,
  AutocompleteOwnerState,
  AutocompleteRenderGetTagProps,
  AutocompleteRenderInputParams,
  Box,
  Button,
  ButtonGroup,
  Popper,
  PopperProps,
} from '@mui/material';
import React from 'react';
import {
  Controller,
  Control,
  FieldValues,
  FieldErrors,
  FieldError,
  UseFormRegister,
} from 'react-hook-form';
import { AutocompleteTextField } from './AutocompleteTextField';
export interface TextFieldProps {
  id: string;
  name?: string;
  defaultValue?: string | number;
  required?: boolean;
  control: Control<FieldValues, unknown>;
  errors?: FieldErrors;
  label?: string;
  options: MultiSelectOption[];
  value?: string | number;
  register?: UseFormRegister<FieldValues>;
  error?: FieldError;
  multiple?: boolean;
  onValueChange?: (
    value: MultiSelectOption | (string | number)[] | null
  ) => void;
  overrideOptionsLabel?: (option: MultiSelectOption) => string;
  allowSelectAll?: boolean;
  addScroll?: boolean;
  limitTags?: number;
  readOnly?: boolean;
  disabled?: boolean;
  disableClearable?: boolean;
  isOptionEqualToValue?: (
    option: MultiSelectOption,
    selectValue: unknown
  ) => boolean;
  // allows to optionally send in a custom tag renderer
  renderTags?:
    | ((
        value: MultiSelectOption[],
        getTagProps: AutocompleteRenderGetTagProps,
        ownerState: AutocompleteOwnerState<
          MultiSelectOption,
          boolean,
          boolean,
          undefined,
          'div'
        >
      ) => React.ReactNode)
    | undefined;
  autoComplete?: 'on' | 'new-password' | 'off';
}

export const SelectField = ({
  id,
  name,
  defaultValue,
  required = false,
  label,
  control,
  errors,
  error,
  options,
  onValueChange,
  multiple = false,
  overrideOptionsLabel,
  allowSelectAll = false,
  addScroll = false,
  limitTags = 20,
  readOnly = false,
  disabled = false,
  disableClearable = false,
  isOptionEqualToValue = (option: MultiSelectOption, selectValue: unknown) => {
    return option.value === selectValue;
  },
  renderTags,
  autoComplete,
}: TextFieldProps) => {
  const [open, setOpen] = React.useState<boolean>(false);

  // MUI owns the listbox ref (it drives keyboard highlight + auto-scroll), so we must
  // not inject our own via ListboxProps — that would clobber MUI's handleListboxRef.
  // Instead, locate the <ul> by the deterministic id MUI assigns it (`${id}-listbox`,
  // enabled by passing `id` to the Autocomplete below).
  const listboxId = `${id}-listbox`;
  const listboxProps: React.HTMLAttributes<HTMLUListElement> = {
    style: { maxHeight: '15rem' },
  };

  // When the popper opens, keep the currently selected option in view instead of
  // letting the listbox scroll back to the top. The scroll is deferred to the next
  // animation frame so MUI has rendered the option <li> elements before we query them.
  React.useLayoutEffect(() => {
    if (!open) {
      return;
    }

    const frame = requestAnimationFrame(() => {
      const listbox = document.getElementById(listboxId);
      if (!listbox) {
        return;
      }

      const selectedOption = listbox.querySelector<HTMLElement>(
        '[aria-selected="true"]'
      );
      // Feature-detect: scrollIntoView is absent in some test environments (jsdom).
      if (typeof selectedOption?.scrollIntoView === 'function') {
        selectedOption.scrollIntoView({ block: 'nearest' });
      }
    });

    return () => cancelAnimationFrame(frame);
  }, [open, listboxId]);

  // Selecting an option in multiple mode keeps the popper open, but MUI resets the
  // highlighted option and scrolls the listbox back to the top on the re-render. Capture
  // the scroll position at selection time and restore it on the next frame (after MUI's
  // layout effects have run) so the list stays where the user just selected.
  const preserveListboxScroll = () => {
    const listbox = document.getElementById(listboxId);
    if (!listbox) {
      return;
    }

    const scrollTop = listbox.scrollTop;
    requestAnimationFrame(() => {
      const restored = document.getElementById(listboxId);
      if (restored) {
        restored.scrollTop = scrollTop;
      }
    });
  };

  const defaultProps = {
    options: [...options],
    getOptionLabel: (option: MultiSelectOption) => option.title,
  };
  const fieldName = name || id;

  const handleMultiChange = (
    onChange: (...event: unknown[]) => void,
    data: MultiSelectOption[]
  ) => {
    onChange(parseData(data));
    onValueChange && onValueChange(parseData(data));
  };

  const parseData = (data: MultiSelectOption[]): (string | number)[] => {
    return data.map((item: MultiSelectOption) => {
      if (typeof item === 'string' || typeof item === 'number') return item;
      return item.value;
    });
  };

  const getOptionLabel = (option: unknown) => {
    const multiSelectOption = option as MultiSelectOption;

    if (overrideOptionsLabel) {
      return overrideOptionsLabel(multiSelectOption);
    }

    if (multiSelectOption?.title) {
      return multiSelectOption.title;
    } else {
      const result = findOption(option);
      if (result) {
        return result.title;
      }
    }
    return '';
  };

  const findOption = (option: unknown) => {
    return options.find(({ value: findValue }) => findValue === option);
  };

  const closePopper = () => setOpen(false);
  const openPopper = () => setOpen(true);

  const handleHeight = multiple ? '2.75rem' : '';

  const handleSelectAll = (
    onChange: (...event: unknown[]) => void,
    selectedOptions: MultiSelectOption[]
  ) => {
    handleMultiChange(onChange, selectedOptions);
  };

  const renderTextField = (params: AutocompleteRenderInputParams) => {
    return (
      <AutocompleteTextField
        params={params}
        autoComplete={autoComplete}
        label={label}
        required={required}
        sx={{
          '.MuiAutocomplete-inputRoot': {
            minHeight: handleHeight,
            width: '100%',
          },
        }}
        error={error ? !!error : !!errors && !!errors[id]?.message}
        helperText={error ? error : !!errors && errors[id]?.message}
      />
    );
  };

  // controller is built in to react-hook-form and it allows your input to be registered with useForms internal state.
  return (
    <Controller
      data-testid="select-component"
      control={control}
      name={fieldName}
      defaultValue={defaultValue}
      render={({ field: { onChange, value } }) => {
        return (
          <>
            <Autocomplete
              {...defaultProps}
              id={id}
              readOnly={readOnly}
              disabled={disabled}
              open={open}
              disablePortal={true}
              disableClearable={disableClearable}
              onOpen={openPopper}
              onClose={closePopper}
              disableCloseOnSelect={multiple}
              limitTags={limitTags}
              ListboxProps={listboxProps}
              componentsProps={{
                paper: {
                  sx: (theme) => ({
                    backgroundColor: theme.palette.background.paper,
                    // Cancel MUI's dark elevation overlay so the dropdown
                    // matches the themed popover instead of rendering light.
                    backgroundImage: 'none',
                    color: theme.palette.text.primary,
                  }),
                },
              }}
              multiple={multiple}
              getOptionLabel={getOptionLabel}
              value={value || (multiple ? [] : null)}
              renderInput={(params) => renderTextField(params)}
              PopperComponent={(props: PopperProps) => (
                <SelectPopper
                  {...props}
                  handleSelectAll={() => handleSelectAll(onChange, options)}
                  allowSelectAll={allowSelectAll}
                />
              )}
              isOptionEqualToValue={isOptionEqualToValue}
              renderTags={renderTags || undefined}
              onChange={(
                _,
                data: MultiSelectOption | MultiSelectOption[] | null
              ) => {
                if (multiple) {
                  // The popper stays open after selecting in multiple mode, so keep the
                  // listbox where it was instead of letting it jump back to the top.
                  preserveListboxScroll();
                  if (!data) {
                    onValueChange?.([]);
                    return onChange('');
                  }
                  handleMultiChange(onChange, data as MultiSelectOption[]);
                } else {
                  if (!data) {
                    onValueChange?.(null);
                    return onChange('');
                  }

                  const d = data as MultiSelectOption;
                  onChange(d.value);
                  onValueChange && onValueChange(d);
                }
              }}
              renderOption={(
                props: React.HTMLAttributes<HTMLLIElement>,
                option: { title: string; value: string | number }
              ) => {
                return (
                  <Box
                    component="li"
                    {...props}
                    key={option.value}
                    sx={{ maxHeight: '100px' }}
                  >
                    {option.title}
                  </Box>
                );
              }}
            />
            {value?.length > 20 && open && addScroll ? (
              <div style={{ height: '31.25rem' }}></div>
            ) : null}
          </>
        );
      }}
    />
  );
};

type SelectPopperProps = {
  allowSelectAll: boolean;
  handleSelectAll: () => void;
} & PopperProps;

const SelectPopper = function ({
  allowSelectAll,
  handleSelectAll,
  ...rest
}: SelectPopperProps) {
  return (
    <Popper {...rest}>
      {allowSelectAll ? (
        <div
          style={{ display: 'flex', width: '100%', justifyContent: 'flex-end' }}
        >
          <ButtonGroup
            color="primary"
            aria-label="outlined primary button group"
          >
            <Button
              color="primary"
              variant="text"
              onClick={() => handleSelectAll?.()}
              onMouseDown={(e) => e.preventDefault()}
            >
              Select All
            </Button>
          </ButtonGroup>
        </div>
      ) : null}
      {rest.children}
    </Popper>
  );
};
